import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import bs58 from "bs58";
import Alert from "~/components/Alert";
import BottomSheet from "~/components/BottomSheet";
import CheckIcon from "~/components/icons/CheckIcon";
import DownloadIcon from "~/components/icons/DownloadIcon";
import useWindowSize from "~/hooks/use-window-size";
import { useCustomPrivy } from "~/hooks/use-custom-privy";
import {
  postTransferFromLink,
  useGetPublicTransactionGetByToAddress,
} from "~/generated/api";
import { useFeeDelegationTransaction } from "~/hooks/use-fee-delegation-transaction";
import {
  createWalletClient,
  http,
  kaia,
  privateKeyToAccount,
} from "@kaiachain/viem-ext";
import { formatUnits } from "viem/utils";
import {
  KAIA_RPC_URL,
  KAIAPAY_VAULT_ADDRESS,
  USDT_ADDRESS,
} from "~/lib/constants";
import { KAIAPAY_VAULT_ABI } from "~/hooks/useKaiaPayTransfer";
import { usePrivy, useUser } from "@privy-io/react-auth";

function getAccountFromCompressed(compressedKey: string) {
  try {
    // base58 디코딩
    const privateKeyBytes = bs58.decode(compressedKey);

    // hex 문자열로 변환하고 0x 접두사 추가
    // @ts-ignore
    const privateKeyHex = Buffer.from(privateKeyBytes).toString("hex");
    const privateKey = `0x${privateKeyHex}` as const;
    const account = privateKeyToAccount(privateKey);
    const publicAddress = account.address;

    return {
      privateKey,
      publicAddress,
    };
  } catch (error) {
    console.log(error);
    throw new Error(`Failed to decompress private key from: ${compressedKey}`);
  }
}

export default function ReceiveLinkWrapper() {
  const { user } = usePrivy();
  useUser();

  // 스마트 월렛 확인
  const smartWallet = user?.linkedAccounts.find(
    (account) => account.type === "smart_wallet"
  );

  // 스마트 월렛이 생성 중인 경우 로딩 화면 표시
  if (user && !smartWallet) {
    return (
      <div className="min-h-screen bg-[#040404] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-10 h-10 border-2 border-white/20 border-t-[#BFF009] rounded-full animate-spin mb-4"></div>
          <p className="text-white/70 text-sm">지갑을 생성중입니다</p>
        </div>
      </div>
    );
  }

  return <ReceiveLink />;
}

function ReceiveLink() {
  const { hash } = useParams<{ hash: string }>();
  const { login, authenticated, user } = useCustomPrivy();

  const { publicAddress, privateKey } = getAccountFromCompressed(hash!);
  console.log({ publicAddress });
  const { data, isLoading } = useGetPublicTransactionGetByToAddress({
    address: publicAddress,
  });

  const navigate = useNavigate();
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const { height: windowHeight } = useWindowSize();

  const logoMt = windowHeight / 2 - 144 - 62;

  useEffect(() => {
    if (!data?.transaction || isLoading) return;
    if (!data.transaction.canCancel) {
      alert("만료되었거나 이미 받기 완료된 링크입니다.");
      navigate("/home", { replace: true });
    }
  }, [data]);

  const handleErrorSheetClose = () => {
    setIsBottomSheetOpen(false);
  };

  const onButtonClick = async () => {
    // 로그인이 안되어있으면 privy 로그인
    setIsButtonLoading(true);
    if (!authenticated) {
      await login(); // await 가 사실상 작동하지 않음
      setIsButtonLoading(false);
      return;
    } else {
      await handleReceive();
      setIsBottomSheetOpen(true);
      setIsButtonLoading(false);
    }
  };

  const { writeContractFD, publicAddress: senderAddress } =
    useFeeDelegationTransaction({
      walletClient: createWalletClient({
        chain: kaia,
        transport: http(KAIA_RPC_URL),
        account: privateKeyToAccount(privateKey as `0x${string}`),
      }),
    });

  const handleErrorSheetButtonClick = async () => {
    setIsBottomSheetOpen(false);
    navigate("/home");
  };

  const handleReceive = async () => {
    if (!data) return;

    const smartWallet = user?.linkedAccounts.find(
      (account) => account.type === "smart_wallet"
    );
    try {
      const { hash } = await writeContractFD({
        address: KAIAPAY_VAULT_ADDRESS,
        value: 0n,
        abi: KAIAPAY_VAULT_ABI,
        functionName: "transferToken",
        args: [
          senderAddress,
          smartWallet?.address as `0x${string}`,
          USDT_ADDRESS as `0x${string}`,
          data.transaction.amount,
          0n,
          smartWallet?.address as `0x${string}`,
        ],
      });

      await postTransferFromLink({
        prevTransactionId: data.transaction.id,
        txHash: hash,
      });
    } catch (error) {
      console.log(error);
      alert("만료되었거나 이미 받기 완료된 링크입니다.");
      navigate("/home", { replace: true });
    }
  };

  if (isLoading || !data) {
    return <div></div>;
  }

  return (
    <div
      className="flex flex-col h-screen gap-2 bg-cover bg-center overflow-hidden"
      style={{
        backgroundImage: "url('/grid-bg.png')",
      }}
    >
      <Alert
        isOpen={true}
        onClose={() => {}}
        icon={<DownloadIcon />}
        title={`${formatUnits(BigInt(data?.transaction.amount!), 6)} USDT`}
        buttonText={authenticated ? "받기" : "3초만에 로그인하기"}
        onButtonClick={onButtonClick}
        overlay={<div className="" />}
        isButtonLoading={isButtonLoading}
      >
        {data?.transaction.senderAlias}님이 보냈어요 <br />
        아래 '받기' 버튼을 눌러야 송금이 완료돼요
      </Alert>

      <div
        className="h-16 flex justify-start items-center pr-4"
        style={{
          backgroundImage: "url('/logo.png')",
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          height: "32px",
          marginTop: `${logoMt}px`,
        }}
      />

      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleErrorSheetClose}
        icon={<CheckIcon />}
        title="돈 받기 완료"
        buttonText="확인"
        onButtonClick={handleErrorSheetButtonClick}
      >
        거래 세부 내역과 트랜잭션 주소는
        <br />
        거래 내역 상세에서 확인하실 수 있어요
      </BottomSheet>
    </div>
  );
}
